/**
 * Command Center Page - Smart Alerts, Goals & Cash Forecast
 * Tier 1 Features for Director-level decision making
 */
import { useState, useEffect } from 'react';
import { 
  AlertTriangle, CheckCircle, XCircle, Target, TrendingUp, TrendingDown,
  DollarSign, Users, Calendar, Clock, Plus, Edit2, Trash2, Save,
  Zap, Brain, PiggyBank, ArrowRight, RefreshCw, ChevronRight,
  AlertCircle, Lightbulb, BarChart3, Activity
} from 'lucide-react';
import { 
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, Cell, PieChart, Pie
} from 'recharts';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = {
  critical: '#EF4444',
  warning: '#F59E0B', 
  positive: '#10B981',
  info: '#3B82F6'
};

const GOAL_CATEGORIES = [
  { id: 'revenue', label: 'Revenue', icon: TrendingUp, color: 'emerald' },
  { id: 'profit', label: 'Profit', icon: DollarSign, color: 'blue' },
  { id: 'expense', label: 'Expense', icon: TrendingDown, color: 'red' },
  { id: 'sales', label: 'Sales', icon: BarChart3, color: 'purple' },
  { id: 'hr', label: 'HR', icon: Users, color: 'amber' },
  { id: 'growth', label: 'Growth', icon: Zap, color: 'pink' }
];

const AUTO_TRACK_OPTIONS = [
  { value: '', label: 'Manual Update' },
  { value: 'revenue_total_month', label: 'Revenue Bulan Ini (Auto)' },
  { value: 'expense_total_month', label: 'Expenses Bulan Ini (Auto)' },
  { value: 'profit_total_month', label: 'Profit Bulan Ini (Auto)' },
  { value: 'staff_count', label: 'Jumlah Staff Aktif (Auto)' }
];

// Modal Component
const Modal = ({ isOpen, onClose, title, children }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-4 border-b border-gray-100 flex items-center justify-between sticky top-0 bg-white z-10">
          <h3 className="font-bold text-gray-800">{title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
        </div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
};

// Alert Card Component
const AlertCard = ({ alert }) => {
  const bgColors = {
    critical: 'bg-red-50 border-red-200',
    warning: 'bg-amber-50 border-amber-200',
    positive: 'bg-emerald-50 border-emerald-200',
    info: 'bg-blue-50 border-blue-200'
  };
  const textColors = {
    critical: 'text-red-700',
    warning: 'text-amber-700',
    positive: 'text-emerald-700',
    info: 'text-blue-700'
  };
  const icons = {
    critical: XCircle,
    warning: AlertTriangle,
    positive: CheckCircle,
    info: AlertCircle
  };
  const Icon = icons[alert.type] || AlertCircle;

  return (
    <div className={`rounded-xl p-4 border ${bgColors[alert.type]}`}>
      <div className="flex items-start gap-3">
        <Icon className={`w-5 h-5 mt-0.5 ${textColors[alert.type]}`} />
        <div className="flex-1">
          <h4 className={`font-semibold text-sm ${textColors[alert.type]}`}>{alert.title}</h4>
          <p className="text-gray-600 text-sm mt-1">{alert.message}</p>
          <div className="mt-2 flex items-center gap-2">
            <ArrowRight className="w-3 h-3 text-gray-400" />
            <span className="text-xs text-gray-500">{alert.action}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Goal Progress Card
const GoalCard = ({ goal, onEdit, onDelete, onUpdateProgress }) => {
  const statusColors = {
    achieved: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    on_track: 'bg-blue-100 text-blue-700 border-blue-200',
    at_risk: 'bg-amber-100 text-amber-700 border-amber-200',
    behind: 'bg-red-100 text-red-700 border-red-200'
  };
  const progressColors = {
    achieved: '#10B981',
    on_track: '#3B82F6',
    at_risk: '#F59E0B',
    behind: '#EF4444'
  };

  return (
    <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${statusColors[goal.status]}`}>
              {goal.status === 'achieved' ? '✓ Achieved' : 
               goal.status === 'on_track' ? '● On Track' :
               goal.status === 'at_risk' ? '⚠ At Risk' : '✗ Behind'}
            </span>
            {goal.auto_track && (
              <span className="px-2 py-0.5 rounded-full text-xs bg-purple-100 text-purple-700">Auto</span>
            )}
          </div>
          <h4 className="font-semibold text-gray-800">{goal.title}</h4>
          {goal.description && <p className="text-xs text-gray-500 mt-1">{goal.description}</p>}
        </div>
        <div className="flex gap-1">
          <button onClick={() => onEdit(goal)} className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-400 hover:text-gray-600">
            <Edit2 className="w-4 h-4" />
          </button>
          <button onClick={() => onDelete(goal.id)} className="p-1.5 hover:bg-red-50 rounded-lg text-gray-400 hover:text-red-600">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="mb-3">
        <div className="flex justify-between text-sm mb-1">
          <span className="text-gray-500">Progress</span>
          <span className="font-bold" style={{ color: progressColors[goal.status] }}>
            {goal.progress_percent}%
          </span>
        </div>
        <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
          <div 
            className="h-full rounded-full transition-all duration-500"
            style={{ 
              width: `${Math.min(goal.progress_percent, 100)}%`,
              backgroundColor: progressColors[goal.status]
            }}
          />
        </div>
      </div>

      {/* Values */}
      <div className="flex justify-between items-center text-sm mb-3">
        <div>
          <span className="text-gray-400">Current: </span>
          <span className="font-semibold text-gray-700">{goal.unit}{goal.current_value?.toLocaleString()}</span>
        </div>
        <div>
          <span className="text-gray-400">Target: </span>
          <span className="font-semibold text-gray-700">{goal.unit}{goal.target_value?.toLocaleString()}</span>
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between pt-3 border-t border-gray-100">
        <div className="flex items-center gap-1 text-xs text-gray-400">
          <Calendar className="w-3 h-3" />
          <span>{goal.days_remaining} hari lagi</span>
        </div>
        {!goal.auto_track && (
          <button 
            onClick={() => {
              const newValue = prompt('Masukkan nilai terkini:', goal.current_value);
              if (newValue !== null) onUpdateProgress(goal.id, parseFloat(newValue));
            }}
            className="text-xs text-blue-600 hover:text-blue-800 font-medium"
          >
            Update Progress
          </button>
        )}
      </div>
    </div>
  );
};

// Goal Form
const GoalForm = ({ onSubmit, onClose, initialData }) => {
  const getDefaultEndDate = () => {
    const date = new Date();
    date.setDate(date.getDate() + 90);
    return date.toISOString().split('T')[0];
  };
  
  const [form, setForm] = useState(initialData || {
    title: '',
    description: '',
    category: 'revenue',
    target_value: '',
    current_value: 0,
    unit: 'RM',
    start_date: new Date().toISOString().split('T')[0],
    end_date: getDefaultEndDate(),
    priority: 'medium',
    owner: '',
    auto_track: false,
    auto_track_source: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      ...form,
      target_value: parseFloat(form.target_value) || 0,
      current_value: parseFloat(form.current_value) || 0
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Nama Goal *</label>
        <input
          type="text"
          value={form.title}
          onChange={(e) => setForm({...form, title: e.target.value})}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
          placeholder="Cth: Capai RM500K revenue Q1"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Deskripsi</label>
        <textarea
          value={form.description}
          onChange={(e) => setForm({...form, description: e.target.value})}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
          rows={2}
          placeholder="Penerangan lanjut..."
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Kategori</label>
          <select
            value={form.category}
            onChange={(e) => setForm({...form, category: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
          >
            {GOAL_CATEGORIES.map(cat => (
              <option key={cat.id} value={cat.id}>{cat.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
          <select
            value={form.priority}
            onChange={(e) => setForm({...form, priority: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
          >
            <option value="critical">🔴 Critical</option>
            <option value="high">🟠 High</option>
            <option value="medium">🟡 Medium</option>
            <option value="low">🟢 Low</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Target *</label>
          <input
            type="number"
            value={form.target_value}
            onChange={(e) => setForm({...form, target_value: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Current</label>
          <input
            type="number"
            value={form.current_value}
            onChange={(e) => setForm({...form, current_value: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Unit</label>
          <select
            value={form.unit}
            onChange={(e) => setForm({...form, unit: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
          >
            <option value="RM">RM</option>
            <option value="$">$</option>
            <option value="%">%</option>
            <option value="">Count</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Tarikh Mula</label>
          <input
            type="date"
            value={form.start_date}
            onChange={(e) => setForm({...form, start_date: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Tarikh Akhir</label>
          <input
            type="date"
            value={form.end_date}
            onChange={(e) => setForm({...form, end_date: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Auto-Track</label>
        <select
          value={form.auto_track_source}
          onChange={(e) => setForm({
            ...form, 
            auto_track_source: e.target.value,
            auto_track: e.target.value !== ''
          })}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
        >
          {AUTO_TRACK_OPTIONS.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
        <p className="text-xs text-gray-400 mt-1">Auto-track akan update nilai secara automatik dari sistem</p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Owner/PIC</label>
        <input
          type="text"
          value={form.owner}
          onChange={(e) => setForm({...form, owner: e.target.value})}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
          placeholder="Nama PIC"
        />
      </div>

      <div className="flex gap-3 pt-2">
        <button type="button" onClick={onClose} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">
          Batal
        </button>
        <button type="submit" className="flex-1 px-4 py-2 bg-red-700 text-white rounded-lg hover:bg-red-800 flex items-center justify-center gap-2">
          <Save className="w-4 h-4" /> Simpan
        </button>
      </div>
    </form>
  );
};

// Main Command Center Page
const CommandCenterPage = () => {
  const [digest, setDigest] = useState(null);
  const [goals, setGoals] = useState([]);
  const [goalsSummary, setGoalsSummary] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingGoal, setEditingGoal] = useState(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [digestRes, goalsRes, summaryRes, forecastRes] = await Promise.all([
        axios.get(`${API}/smart-digest`),
        axios.get(`${API}/goals`),
        axios.get(`${API}/goals/summary`),
        axios.get(`${API}/smart-digest/cashflow-forecast`)
      ]);
      setDigest(digestRes.data);
      setGoals(goalsRes.data);
      setGoalsSummary(summaryRes.data);
      setForecast(forecastRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleAddGoal = async (data) => {
    try {
      await axios.post(`${API}/goals`, data);
      await fetchData();
      setModalOpen(false);
    } catch (error) {
      console.error('Error adding goal:', error);
      alert('Gagal menambah goal');
    }
  };

  const handleEditGoal = async (data) => {
    try {
      await axios.put(`${API}/goals/${editingGoal.id}`, data);
      await fetchData();
      setEditingGoal(null);
      setModalOpen(false);
    } catch (error) {
      console.error('Error updating goal:', error);
      alert('Gagal mengemaskini goal');
    }
  };

  const handleDeleteGoal = async (id) => {
    if (window.confirm('Padam goal ini?')) {
      try {
        await axios.delete(`${API}/goals/${id}`);
        await fetchData();
      } catch (error) {
        console.error('Error deleting goal:', error);
      }
    }
  };

  const handleUpdateProgress = async (id, value) => {
    try {
      await axios.post(`${API}/goals/${id}/update-progress?new_value=${value}`);
      await fetchData();
    } catch (error) {
      console.error('Error updating progress:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-700"></div>
      </div>
    );
  }

  const summary = digest?.summary || {};
  const alerts = digest?.alerts || [];
  const recommendations = digest?.recommendations || [];

  return (
    <div className="space-y-6" data-testid="command-center-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-gray-900 via-red-900 to-gray-900 rounded-2xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-3">
              <Brain className="w-8 h-8" /> Command Center
            </h1>
            <p className="text-gray-300 text-sm mt-1">Smart Alerts • Goals & OKR • Cash Forecast</p>
          </div>
          <div className="text-right">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-gray-400 text-sm">Business Health</span>
              <button onClick={fetchData} className="p-2 hover:bg-white/10 rounded">
                <RefreshCw className="w-4 h-4" />
              </button>
            </div>
            <div className="flex items-center gap-2">
              <span className={`text-4xl font-bold ${
                summary.health_score >= 70 ? 'text-emerald-400' :
                summary.health_score >= 50 ? 'text-amber-400' : 'text-red-400'
              }`}>
                {summary.health_score || 0}
              </span>
              <span className="text-gray-400">/100</span>
            </div>
            <span className={`text-sm ${
              summary.health_color === 'green' ? 'text-emerald-400' :
              summary.health_color === 'amber' ? 'text-amber-400' : 'text-red-400'
            }`}>
              {summary.health_status}
            </span>
          </div>
        </div>
      </div>

      {/* Alerts Section */}
      <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-500" /> Smart Alerts
          </h2>
          <div className="flex gap-2">
            {digest?.critical_alerts > 0 && (
              <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm font-medium">
                {digest.critical_alerts} Critical
              </span>
            )}
            {digest?.warning_alerts > 0 && (
              <span className="px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-sm font-medium">
                {digest.warning_alerts} Warning
              </span>
            )}
          </div>
        </div>
        
        {alerts.length > 0 ? (
          <div className="grid md:grid-cols-2 gap-4">
            {alerts.slice(0, 6).map((alert, idx) => (
              <AlertCard key={idx} alert={alert} />
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-400">
            <CheckCircle className="w-12 h-12 mx-auto mb-2 text-emerald-400" />
            <p>Tiada alert kritikal buat masa ini. Semua baik!</p>
          </div>
        )}
      </div>

      {/* Goals Section */}
      <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
            <Target className="w-5 h-5 text-red-700" /> Company Goals & OKR
          </h2>
          <button
            onClick={() => { setEditingGoal(null); setModalOpen(true); }}
            className="px-4 py-2 bg-red-700 text-white rounded-lg hover:bg-red-800 flex items-center gap-2 text-sm"
          >
            <Plus className="w-4 h-4" /> Tambah Goal
          </button>
        </div>

        {/* Goals Summary */}
        {goalsSummary && goalsSummary.total_goals > 0 && (
          <div className="grid grid-cols-4 gap-4 mb-6">
            <div className="bg-emerald-50 rounded-xl p-4 text-center">
              <p className="text-2xl font-bold text-emerald-600">{goalsSummary.achieved}</p>
              <p className="text-xs text-emerald-700">Achieved</p>
            </div>
            <div className="bg-blue-50 rounded-xl p-4 text-center">
              <p className="text-2xl font-bold text-blue-600">{goalsSummary.on_track}</p>
              <p className="text-xs text-blue-700">On Track</p>
            </div>
            <div className="bg-amber-50 rounded-xl p-4 text-center">
              <p className="text-2xl font-bold text-amber-600">{goalsSummary.at_risk}</p>
              <p className="text-xs text-amber-700">At Risk</p>
            </div>
            <div className="bg-red-50 rounded-xl p-4 text-center">
              <p className="text-2xl font-bold text-red-600">{goalsSummary.behind}</p>
              <p className="text-xs text-red-700">Behind</p>
            </div>
          </div>
        )}

        {/* Goals Grid */}
        {goals.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {goals.map(goal => (
              <GoalCard
                key={goal.id}
                goal={goal}
                onEdit={(g) => { setEditingGoal(g); setModalOpen(true); }}
                onDelete={handleDeleteGoal}
                onUpdateProgress={handleUpdateProgress}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-400">
            <Target className="w-12 h-12 mx-auto mb-2" />
            <p>Belum ada goals. Klik &quot;Tambah Goal&quot; untuk mula.</p>
          </div>
        )}
      </div>

      {/* Cash Flow Forecast */}
      <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
            <PiggyBank className="w-5 h-5 text-blue-600" /> Cash Flow Forecast (12 Bulan)
          </h2>
          {forecast?.needs_attention && (
            <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm font-medium animate-pulse">
              ⚠️ Perlu Perhatian
            </span>
          )}
        </div>

        {forecast && (
          <>
            {/* Key Metrics */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <div className="bg-blue-50 rounded-xl p-4">
                <p className="text-xs text-blue-600 mb-1">Cash Balance</p>
                <p className="text-xl font-bold text-blue-700">RM{forecast.current_balance?.toLocaleString()}</p>
              </div>
              <div className="bg-emerald-50 rounded-xl p-4">
                <p className="text-xs text-emerald-600 mb-1">Monthly Inflow</p>
                <p className="text-xl font-bold text-emerald-700">+RM{forecast.monthly_inflow?.toLocaleString()}</p>
              </div>
              <div className="bg-red-50 rounded-xl p-4">
                <p className="text-xs text-red-600 mb-1">Monthly Outflow</p>
                <p className="text-xl font-bold text-red-700">-RM{forecast.monthly_outflow?.toLocaleString()}</p>
              </div>
              <div className={`rounded-xl p-4 ${forecast.runway_months < 6 ? 'bg-red-50' : 'bg-emerald-50'}`}>
                <p className={`text-xs mb-1 ${forecast.runway_months < 6 ? 'text-red-600' : 'text-emerald-600'}`}>Cash Runway</p>
                <p className={`text-xl font-bold ${forecast.runway_months < 6 ? 'text-red-700' : 'text-emerald-700'}`}>
                  {forecast.runway_months > 12 ? '12+' : forecast.runway_months} bulan
                </p>
              </div>
            </div>

            {/* Forecast Chart */}
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={forecast.forecast}>
                  <defs>
                    <linearGradient id="colorBalance" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#3B82F6" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `RM${(v/1000).toFixed(0)}k`} />
                  <Tooltip formatter={(v) => [`RM${v.toLocaleString()}`, 'Projected Balance']} />
                  <Area 
                    type="monotone" 
                    dataKey="projected_balance" 
                    stroke="#3B82F6" 
                    fillOpacity={1} 
                    fill="url(#colorBalance)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            {/* Danger Months Warning */}
            {forecast.danger_months?.length > 0 && (
              <div className="mt-4 bg-red-50 border border-red-200 rounded-xl p-4">
                <div className="flex items-center gap-2 text-red-700">
                  <AlertTriangle className="w-5 h-5" />
                  <span className="font-semibold">Danger Zone Detected!</span>
                </div>
                <p className="text-sm text-red-600 mt-1">
                  Cash dijangka habis pada: <strong>{forecast.danger_months.join(', ')}</strong>
                </p>
              </div>
            )}

            {/* Recommendations */}
            <div className="mt-4 bg-gray-50 rounded-xl p-4">
              <p className="text-sm font-medium text-gray-700 mb-2">💡 Cadangan:</p>
              <ul className="space-y-1">
                {forecast.recommendations?.map((rec, idx) => (
                  <li key={idx} className="text-sm text-gray-600 flex items-center gap-2">
                    <ChevronRight className="w-3 h-3" /> {rec}
                  </li>
                ))}
              </ul>
            </div>
          </>
        )}
      </div>

      {/* AI Recommendations */}
      <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-2xl p-6 border border-purple-100">
        <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2 mb-4">
          <Lightbulb className="w-5 h-5 text-purple-600" /> AI Recommendations
        </h2>
        
        <div className="grid md:grid-cols-2 gap-4">
          {recommendations.map((rec, idx) => (
            <div key={idx} className="bg-white rounded-xl p-4 shadow-sm">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-semibold text-gray-800">{rec.title}</h4>
                <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                  rec.impact === 'high' ? 'bg-red-100 text-red-700' :
                  rec.impact === 'medium' ? 'bg-amber-100 text-amber-700' :
                  'bg-blue-100 text-blue-700'
                }`}>
                  {rec.impact} impact
                </span>
              </div>
              <p className="text-sm text-gray-600 mb-3">{rec.description}</p>
              <ul className="space-y-1">
                {rec.actions?.slice(0, 3).map((action, i) => (
                  <li key={i} className="text-xs text-gray-500 flex items-center gap-2">
                    <ArrowRight className="w-3 h-3" /> {action}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* Goal Modal */}
      <Modal
        isOpen={modalOpen}
        onClose={() => { setModalOpen(false); setEditingGoal(null); }}
        title={editingGoal ? 'Edit Goal' : 'Tambah Goal Baru'}
      >
        <GoalForm
          onSubmit={editingGoal ? handleEditGoal : handleAddGoal}
          onClose={() => { setModalOpen(false); setEditingGoal(null); }}
          initialData={editingGoal}
        />
      </Modal>
    </div>
  );
};

export default CommandCenterPage;
